import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { HiCheck } from 'react-icons/hi';
import api from 'api';
import type { UserDocument } from 'api/rest/user_documents';
import {
    Modal,
    ModalHeader,
    Form,
    Section,
    Actions,
    PrimaryButton,
    SecondaryButton,
} from 'components/common/Modal';
import UrlDocumentPreview from 'components/v2/common/documentPreview';
import { useWindowSize } from 'hooks/useWindowSize';
import { RootState } from 'redux/store';
import * as S from './InitialDocuments.styled';
import {
    INITIAL_DOCUMENTS_REFRESH_EVENT,
    InitialDocumentsRefreshDetail,
} from './initialDocumentsEvents';

/**
 * The three user-level document templates this card surfaces. The `name_en`
 * value MUST match the `DocumentTemplate.name_en` configured on the backend
 * exactly — that's what both the GET and POST endpoints key off of.
 */
type InitialDocKey = 'passport' | 'resume' | 'proofOfAddress';

interface InitialDocConfig {
    key: InitialDocKey;
    nameEn: string;
    /** Translation key for the displayed label (with English fallback). */
    labelKey: string;
    labelFallback: string;
}

const INITIAL_DOCS: InitialDocConfig[] = [
    {
        key: 'passport',
        nameEn: 'Passport',
        labelKey: 'passport',
        labelFallback: 'Passport',
    },
    {
        key: 'resume',
        nameEn: 'Resume',
        labelKey: 'resume',
        labelFallback: 'Resume',
    },
    {
        key: 'proofOfAddress',
        nameEn: 'Proof of address',
        labelKey: 'proofOfAddress',
        labelFallback: 'Proof of address',
    },
];

type InitialDocsState = Record<InitialDocKey, UserDocument | null>;
type UploadingState = Record<InitialDocKey, boolean>;
type ErrorsState = Record<InitialDocKey, string | null>;
type ViewingState = Record<InitialDocKey, boolean>;

const emptyDocsState = (): InitialDocsState => ({
    passport: null,
    resume: null,
    proofOfAddress: null,
});

const emptyUploadingState = (): UploadingState => ({
    passport: false,
    resume: false,
    proofOfAddress: false,
});

const emptyErrorsState = (): ErrorsState => ({
    passport: null,
    resume: null,
    proofOfAddress: null,
});

const emptyViewingState = (): ViewingState => ({
    passport: false,
    resume: false,
    proofOfAddress: false,
});

/** A document is "provided" when the backend returned a non-null resource_name. */
const hasResource = (doc: UserDocument | null | undefined): boolean =>
    !!doc && typeof doc.resource_name === 'string' && doc.resource_name.length > 0;

type Props = {
    /**
     * Candidate (user) id whose documents are managed by this card. When
     * omitted the component resolves the id itself, in priority order:
     *   1. `:id` URL param (e.g. on `/dashboard/:id/client-profile`)
     *   2. `activeApp.candidate_id` from redux
     * Pass an explicit value when the caller already knows the candidate
     * (e.g. the onboarding sidebar parses it from a different URL shape).
     */
    userId?: number | string | null;
};

/**
 * Card showing the status of the three user-level documents (Passport, Resume,
 * Proof of address) tied to DocumentTemplate.name_en. Styled to match the
 * Client Engagement card so the two surfaces stack cleanly inside the
 * dashboard right-rail.
 *
 * Card behavior:
 *  - Provided rows can be previewed by clicking the document name, replaced,
 *    or attached to a case.
 *  - Missing rows render an inline "Upload" button.
 *  - Upload/Replace buttons open a hidden file input and POST the selection to
 *    `/api/users/:user_id/documents` via `api.userDocuments.post`.
 */
const InitialDocuments = ({ userId: userIdProp }: Props = {}) => {
    const { t } = useTranslation();
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const applications = useSelector((state: RootState) => state.applications.applications);
    const { id: candidateIdFromUrl } = useParams<{ id?: string }>();

    /**
     * Userid resolution order (highest -> lowest priority):
     *   1. Explicit `userId` prop — caller-overridden (used by
     *      `OnboardingButtons`, which parses the assessment/agreement URL
     *      itself so the card is scoped to the selected client).
     *   2. `:id` URL param — when the dashboard right-rail is mounted on
     *      `/dashboard/:id/client-profile`, the URL is the source of
     *      truth for which client the user is viewing. Using it directly
     *      means the card refetches the moment the user picks a new
     *      client in `SelectClientSearchBar`, even when the new client
     *      has no applications (in which case `loadApp` never fires and
     *      `activeApp` stays stuck on the previous candidate).
     *   3. `activeApp.candidate_id` — legacy fallback for screens where
     *      no `:id` param is present in the URL but an active app is
     *      selected (e.g. the bare `/dashboard` overview where the user
     *      has clicked a row in the cases table).
     */
    const userId =
        userIdProp ??
        (candidateIdFromUrl && candidateIdFromUrl !== '0' ? candidateIdFromUrl : null) ??
        activeApp?.candidate_id ??
        null;

    // Cases (applications) that belong to the candidate this card is showing.
    // `applications` in redux can briefly hold the previous candidate's cases
    // while a new client is loading, so we filter defensively.
    const candidateCases = useMemo(() => {
        if (!userId) return [];
        const target = userId.toString();
        return (applications || []).filter((app) => app.candidate_id.toString() === target);
    }, [applications, userId]);

    const [docs, setDocs] = useState<InitialDocsState>(emptyDocsState);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [uploading, setUploading] = useState<UploadingState>(emptyUploadingState);
    const [uploadErrors, setUploadErrors] = useState<ErrorsState>(emptyErrorsState);
    const [viewing, setViewing] = useState<ViewingState>(emptyViewingState);

    // Inline preview dialog state. Clicking a provided document name
    // resolves a signed URL from `/users/:user_id/documents/:doc_id/resource_uri`
    // and renders the document inside the shared `UrlDocumentPreview` modal
    // viewer (handles PDF / image / DOCX inline).
    const [previewTarget, setPreviewTarget] = useState<{
        url: string;
        fileName: string;
        title: string;
    } | null>(null);
    const [width] = useWindowSize();
    const isMobileViewport = width < 769;

    // Case-picker modal state. `copyTarget` holds the document the user chose
    // to attach; `copying` tracks the in-flight POST.
    const [copyTarget, setCopyTarget] = useState<{
        docId: number;
        label: string;
    } | null>(null);
    const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
    const [copying, setCopying] = useState<boolean>(false);
    const [copyError, setCopyError] = useState<string | null>(null);

    // One hidden <input type="file"> per row so each Upload/Replace button can
    // open its own picker without a third-party component.
    const fileInputRefs = useRef<Record<InitialDocKey, HTMLInputElement | null>>({
        passport: null,
        resume: null,
        proofOfAddress: null,
    });

    const fetchAll = useCallback(async () => {
        if (!userId) {
            setDocs(emptyDocsState());
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const results = await Promise.all(
                INITIAL_DOCS.map((cfg) => api.userDocuments.get(userId, cfg.nameEn, 'USER'))
            );
            const next: InitialDocsState = emptyDocsState();
            INITIAL_DOCS.forEach((cfg, idx) => {
                const data = results[idx]?.data;
                // GET returns `{}` when no doc exists. Normalise that to `null`
                // so downstream checks (`hasResource`) stay simple.
                next[cfg.key] = data && Object.keys(data).length > 0 ? data : null;
            });
            setDocs(next);
        } catch (e) {
            console.error('Failed to load required user documents', e);
            setError(true);
            setDocs(emptyDocsState());
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    /**
     * Subscribe to cross-tree refresh signals from features that mutate the
     * user's documents server-side (e.g. the Auto Fill Form CV upload on the
     * assessment form, which writes a Resume document on the backend). When
     * the event fires we refetch so the row state reflects the new server
     * state. The event's `userId` scope is honoured: if it's set we only
     * refetch when it matches our `userId`; if it's omitted we refetch
     * unconditionally.
     */
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<InitialDocumentsRefreshDetail>).detail;
            if (
                detail?.userId != null &&
                userId != null &&
                String(detail.userId) !== String(userId)
            ) {
                return;
            }
            fetchAll();
        };
        window.addEventListener(INITIAL_DOCUMENTS_REFRESH_EVENT, handler);
        return () => window.removeEventListener(INITIAL_DOCUMENTS_REFRESH_EVENT, handler);
    }, [fetchAll, userId]);

    const providedCount = useMemo(
        () =>
            INITIAL_DOCS.reduce(
                (count, cfg) => (hasResource(docs[cfg.key]) ? count + 1 : count),
                0
            ),
        [docs]
    );

    if (!userId) {
        // Render nothing while a candidate context is being resolved — keeps
        // the right-rail clean and avoids flashing an empty/error state.
        return null;
    }

    const eyebrowLabel = t('initialDocuments', {
        lng: language,
        defaultValue: 'Initial Documents',
    });

    const compactHeader = (
        <S.Header>
            <S.Eyebrow>{eyebrowLabel}</S.Eyebrow>
        </S.Header>
    );

    if (loading) {
        return (
            <S.CardStatic aria-busy="true">
                {compactHeader}
                <S.SkeletonRow>
                    <S.SkeletonBlock width="80px" height="14px" radius="6px" />
                    <S.SkeletonBlock width="96px" height="18px" radius="999px" />
                </S.SkeletonRow>
                <S.SkeletonRow>
                    <S.SkeletonBlock width="80px" height="14px" radius="6px" />
                    <S.SkeletonBlock width="96px" height="18px" radius="999px" />
                </S.SkeletonRow>
                <S.SkeletonRow>
                    <S.SkeletonBlock width="80px" height="14px" radius="6px" />
                    <S.SkeletonBlock width="96px" height="18px" radius="999px" />
                </S.SkeletonRow>
            </S.CardStatic>
        );
    }

    if (error) {
        return (
            <S.CardStatic>
                {compactHeader}
                <S.ErrorBanner role="alert">
                    {t('initialDocumentsError', {
                        lng: language,
                        defaultValue:
                            'We couldn\u2019t load required documents right now. Please try again later.',
                    })}
                </S.ErrorBanner>
            </S.CardStatic>
        );
    }

    const handlePickClick = (key: InitialDocKey) => {
        fileInputRefs.current[key]?.click();
    };

    const handleFileChange =
        (cfg: InitialDocConfig) => async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0] ?? null;
            // Reset the input so re-selecting the same file re-triggers change.
            event.target.value = '';
            if (!file || !userId) return;

            setUploading((prev) => ({ ...prev, [cfg.key]: true }));
            setUploadErrors((prev) => ({ ...prev, [cfg.key]: null }));
            try {
                const resp = await api.userDocuments.post(userId, cfg.nameEn, file);
                setDocs((prev) => ({ ...prev, [cfg.key]: resp.data ?? null }));
            } catch (e) {
                console.error(`Failed to upload ${cfg.nameEn} for user ${userId}`, e);
                setUploadErrors((prev) => ({
                    ...prev,
                    [cfg.key]: t('initialDocumentsUploadFailed', {
                        lng: language,
                        defaultValue: 'Upload failed. Please try again.',
                    }),
                }));
            } finally {
                setUploading((prev) => ({ ...prev, [cfg.key]: false }));
            }
        };

    const handleViewDocument = async (cfg: InitialDocConfig) => {
        const doc = docs[cfg.key];
        if (!doc?.id || !userId) return;
        setViewing((prev) => ({ ...prev, [cfg.key]: true }));
        try {
            const resp = await api.userDocuments.getResourceUri(userId, doc.id);
            const url = resp.data?.url;
            if (!url) {
                throw new Error('Missing resource url');
            }
            const label = t(cfg.labelKey, {
                lng: language,
                defaultValue: cfg.labelFallback,
            });
            // Show the document inline via the shared preview Dialog (handles
            // PDF / image / DOCX without leaving the page).
            setPreviewTarget({
                url,
                fileName: doc.resource_name || `${cfg.nameEn}`,
                title: label,
            });
        } catch (e) {
            console.error(`Failed to view ${cfg.nameEn} for user ${userId}`, e);
        } finally {
            setViewing((prev) => ({ ...prev, [cfg.key]: false }));
        }
    };

    const handleClosePreview = () => setPreviewTarget(null);

    const handleOpenCopyToCase = (cfg: InitialDocConfig, doc: UserDocument | null) => {
        if (!doc?.id) return;
        // Preselect the currently active case so a single click confirms the
        // common path (copy onto the case the user is already viewing).
        setSelectedCaseId(activeApp?.id ?? null);
        setCopyError(null);
        setCopyTarget({
            docId: doc.id,
            label: t(cfg.labelKey, {
                lng: language,
                defaultValue: cfg.labelFallback,
            }),
        });
    };

    const handleCloseCopyToCase = () => {
        if (copying) return;
        setCopyTarget(null);
        setSelectedCaseId(null);
        setCopyError(null);
    };

    const handleConfirmCopyToCase = async () => {
        if (!copyTarget || !selectedCaseId) return;
        setCopying(true);
        setCopyError(null);
        try {
            await api.userDocuments.copyToApplication(copyTarget.docId, selectedCaseId);
            setCopyTarget(null);
            setSelectedCaseId(null);
        } catch (e) {
            console.error(
                `Failed to copy document ${copyTarget.docId} to application ${selectedCaseId}`,
                e
            );
            setCopyError(
                t('initialDocumentsCopyFailed', {
                    lng: language,
                    defaultValue: 'Copy failed. Please try again.',
                })
            );
        } finally {
            setCopying(false);
        }
    };

    return (
        <>
            <S.Card aria-label={`${eyebrowLabel}: ${providedCount} / ${INITIAL_DOCS.length}`}>
                <S.Header>
                    <S.Eyebrow>{eyebrowLabel}</S.Eyebrow>
                    <S.HeaderRight>
                        <S.SummaryHint>
                            {providedCount} / {INITIAL_DOCS.length}
                        </S.SummaryHint>
                    </S.HeaderRight>
                </S.Header>
                <S.DocList>
                    {INITIAL_DOCS.map((cfg) => {
                        const doc = docs[cfg.key];
                        const provided = hasResource(doc);
                        const isUploading = uploading[cfg.key];
                        const uploadError = uploadErrors[cfg.key];
                        const isOpeningPreview = viewing[cfg.key];
                        const label = t(cfg.labelKey, {
                            lng: language,
                            defaultValue: cfg.labelFallback,
                        });
                        return (
                            <S.DocRow key={cfg.key}>
                                {provided ? (
                                    <S.DocNameLink
                                        type="button"
                                        disabled={isOpeningPreview}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleViewDocument(cfg);
                                        }}
                                        aria-label={t('viewDocumentNamed', {
                                            lng: language,
                                            name: label,
                                            defaultValue: `View ${label}`,
                                        })}
                                    >
                                        {label}
                                    </S.DocNameLink>
                                ) : (
                                    <S.DocName>{label}</S.DocName>
                                )}
                                <S.DocAction>
                                    {provided && (
                                        <S.RowAttachButton
                                            type="button"
                                            data-row-hover
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleOpenCopyToCase(cfg, doc);
                                            }}
                                            aria-label={t('attachDocumentToCase', {
                                                lng: language,
                                                name: label,
                                                defaultValue: `Attach ${label} to a case`,
                                            })}
                                        >
                                            {t('attachToCase', {
                                                lng: language,
                                                defaultValue: 'Attach to case',
                                            })}
                                        </S.RowAttachButton>
                                    )}
                                    {provided ? (
                                        <S.ReplaceBadgeButton
                                            type="button"
                                            disabled={isUploading}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handlePickClick(cfg.key);
                                            }}
                                            aria-label={t('replaceDocumentNamed', {
                                                lng: language,
                                                name: label,
                                                defaultValue: `Replace ${label}`,
                                            })}
                                        >
                                            {isUploading
                                                ? t('uploading', {
                                                      lng: language,
                                                      defaultValue: 'Uploading\u2026',
                                                  })
                                                : t('replace', {
                                                      lng: language,
                                                      defaultValue: 'Replace',
                                                  })}
                                        </S.ReplaceBadgeButton>
                                    ) : (
                                        <S.UploadButton
                                            type="button"
                                            disabled={isUploading}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handlePickClick(cfg.key);
                                            }}
                                            aria-label={t('uploadDocumentNamed', {
                                                lng: language,
                                                name: label,
                                                defaultValue: `Upload ${label}`,
                                            })}
                                        >
                                            {isUploading
                                                ? t('uploading', {
                                                      lng: language,
                                                      defaultValue: 'Uploading\u2026',
                                                  })
                                                : t('upload', {
                                                      lng: language,
                                                      defaultValue: 'Upload',
                                                  })}
                                        </S.UploadButton>
                                    )}
                                    {uploadError && (
                                        <S.InlineError role="alert">{uploadError}</S.InlineError>
                                    )}
                                </S.DocAction>
                            </S.DocRow>
                        );
                    })}
                </S.DocList>
            </S.Card>

            {/* Hidden file inputs live outside the card so row buttons can
                trigger the native file picker by document key. */}
            {INITIAL_DOCS.map((cfg) => (
                <S.HiddenFileInput
                    key={`file-input-${cfg.key}`}
                    ref={(el) => {
                        fileInputRefs.current[cfg.key] = el;
                    }}
                    type="file"
                    accept="application/pdf,image/*,.doc,.docx"
                    onChange={handleFileChange(cfg)}
                />
            ))}

            {/* Case picker for attaching a provided document to an application. */}
            <Modal
                open={!!copyTarget}
                onClose={handleCloseCopyToCase}
                size="sm"
                ariaLabel={t('copyToCase', {
                    lng: language,
                    defaultValue: 'Attach to case',
                })}
            >
                <Form
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleConfirmCopyToCase();
                    }}
                >
                    <ModalHeader
                        eyebrow={t('copyToCase', {
                            lng: language,
                            defaultValue: 'Attach to case',
                        })}
                        title={
                            copyTarget
                                ? t('attachDocumentToCaseTitle', {
                                      lng: language,
                                      name: copyTarget.label,
                                      defaultValue: `Attach ${copyTarget.label} to a case`,
                                  })
                                : t('copyToCase', {
                                      lng: language,
                                      defaultValue: 'Attach to case',
                                  })
                        }
                        subtitle={t('attachToCaseSubtitle', {
                            lng: language,
                            defaultValue:
                                'Select the case that should receive an attachment of this document.',
                        })}
                    />

                    <Section>
                        {candidateCases.length === 0 ? (
                            <S.EmptyCases>
                                {t('noCasesYet', {
                                    lng: language,
                                    defaultValue: 'No cases yet',
                                })}
                            </S.EmptyCases>
                        ) : (
                            <S.CaseList>
                                {candidateCases.map((app) => {
                                    const selected = selectedCaseId === app.id;
                                    return (
                                        <S.CaseListItem key={app.id}>
                                            <S.CaseOption
                                                type="button"
                                                selected={selected}
                                                disabled={copying}
                                                onClick={() => setSelectedCaseId(app.id)}
                                                title={app.program_name}
                                                aria-pressed={selected}
                                            >
                                                <S.CaseOptionLabel>
                                                    {app.program_name}
                                                </S.CaseOptionLabel>
                                                {selected && (
                                                    <S.CaseOptionCheck aria-hidden="true">
                                                        <HiCheck />
                                                    </S.CaseOptionCheck>
                                                )}
                                            </S.CaseOption>
                                        </S.CaseListItem>
                                    );
                                })}
                            </S.CaseList>
                        )}
                        {copyError && (
                            <S.ModalInlineError role="alert">{copyError}</S.ModalInlineError>
                        )}
                    </Section>

                    <Actions>
                        <SecondaryButton
                            type="button"
                            disabled={copying}
                            onClick={handleCloseCopyToCase}
                        >
                            {t('cancel', { lng: language, defaultValue: 'Cancel' })}
                        </SecondaryButton>
                        <PrimaryButton
                            type="submit"
                            disabled={copying || !selectedCaseId || candidateCases.length === 0}
                        >
                            {copying
                                ? t('copying', {
                                      lng: language,
                                      defaultValue: 'Copying\u2026',
                                  })
                                : t('confirm', {
                                      lng: language,
                                      defaultValue: 'Confirm',
                                  })}
                        </PrimaryButton>
                    </Actions>
                </Form>
            </Modal>

            {/* Inline document viewer (PDF / image / DOCX). The signed URL comes from
                `/users/:user_id/documents/:doc_id/resource_uri` and the
                shared `UrlDocumentPreview` Dialog handles rendering. */}
            <UrlDocumentPreview
                open={!!previewTarget}
                mobile={isMobileViewport}
                onClose={handleClosePreview}
                fileUrl={previewTarget?.url ?? null}
                fileName={previewTarget?.fileName}
                title={previewTarget?.title}
            />
        </>
    );
};

export default InitialDocuments;
